import React from "react";

/**
 * Inline SVG icon set for the Claude Agent surface.
 * All icons inherit `currentColor` and accept a `size` prop (default 16).
 * Keeping them as React components instead of a sprite means no extra fetch.
 */

const base = (size) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.4,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
});

export const IconChat = ({ size = 14 }) => (
  <svg {...base(size)}>
    <path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-7l-4 3.5V17H6a2 2 0 0 1-2-2V6Z" />
    <path d="M8 9.5h8M8 12.5h5" />
  </svg>
);

export const IconFolder = ({ size = 14 }) => (
  <svg {...base(size)}>
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
  </svg>
);

export const IconBook = ({ size = 14 }) => (
  <svg {...base(size)}>
    <path d="M4 5a2 2 0 0 1 2-2h11v16H7a3 3 0 0 0-3 3V5Z" />
    <path d="M9 8h6M9 11h6" />
  </svg>
);

export const IconBulb = ({ size = 14 }) => (
  <svg {...base(size)}>
    <path d="M9 18h6" />
    <path d="M10 21h4" />
    <path d="M12 3a6 6 0 0 0-4 10.5c.6.6 1 1.3 1.2 2.1l.3 1.4h5l.3-1.4c.2-.8.6-1.5 1.2-2.1A6 6 0 0 0 12 3Z" />
  </svg>
);

export const IconAttachment = ({ size = 14 }) => (
  <svg {...base(size)}>
    <path d="m21 11.5-8.5 8.5a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-3-3l8-8" />
  </svg>
);

export const IconPerson = ({ size = 14 }) => (
  <svg {...base(size)}>
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6" />
  </svg>
);

export const IconPeople = ({ size = 14 }) => (
  <svg {...base(size)}>
    <circle cx="9" cy="9" r="3" />
    <path d="M3 19c0-3 2.5-5 6-5s6 2 6 5" />
    <circle cx="17" cy="8" r="2.5" />
    <path d="M15.5 14.5c2.5.4 5 2 5 4.5" />
  </svg>
);

export const IconShare = ({ size = 14 }) => (
  <svg {...base(size)}>
    <circle cx="18" cy="5" r="2.5" />
    <circle cx="6" cy="12" r="2.5" />
    <circle cx="18" cy="19" r="2.5" />
    <path d="m8.2 11 7.6-4.5M8.2 13l7.6 4.5" />
  </svg>
);

export const IconEdit = ({ size = 14 }) => (
  <svg {...base(size)}>
    <path d="M14 4 4 14v6h6L20 10" />
    <path d="m14 4 4 4" />
  </svg>
);

export const IconTrash = ({ size = 14 }) => (
  <svg {...base(size)}>
    <path d="M4 7h16" />
    <path d="M9 7V4h6v3" />
    <path d="M6 7v13a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7" />
    <path d="M10 11v7M14 11v7" />
  </svg>
);

export const IconClose = ({ size = 14 }) => (
  <svg {...base(size)}>
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

export const IconWarning = ({ size = 14 }) => (
  <svg {...base(size)}>
    <path d="M12 3 2.5 20h19L12 3Z" />
    <path d="M12 10v5M12 18v.5" />
  </svg>
);

export const IconWave = ({ size = 14 }) => (
  <svg {...base(size)}>
    <path d="M5 12V7a2 2 0 0 1 4 0v5" />
    <path d="M9 12V5a2 2 0 0 1 4 0v7" />
    <path d="M13 12V7a2 2 0 0 1 4 0v8a6 6 0 0 1-6 6 6 6 0 0 1-6-6v-2a2 2 0 0 1 4 0" />
  </svg>
);

export const IconGlobe = ({ size = 14 }) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a13 13 0 0 1 0 18M12 3a13 13 0 0 0 0 18" />
  </svg>
);

export const IconPlus = ({ size = 14 }) => (
  <svg {...base(size)}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const IconCheck = ({ size = 14 }) => (
  <svg {...base(size)}>
    <path d="m5 12 5 5 9-11" />
  </svg>
);

export const IconDots = ({ size = 14 }) => (
  <svg {...base(size)}>
    <circle cx="12" cy="5" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="12" cy="19" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

export const IconAgent = ({ size = 14 }) => (
  <svg {...base(size)}>
    <rect x="4" y="6" width="16" height="12" rx="3" />
    <circle cx="9" cy="12" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="15" cy="12" r="1.2" fill="currentColor" stroke="none" />
    <path d="M12 3v3" />
  </svg>
);

export const IconClock = ({ size = 14 }) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v4.8l3 2.2" />
  </svg>
);

export const IconInfo = ({ size = 14 }) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 7.75v.5" />
  </svg>
);

export const IconPlay = ({ size = 14 }) => (
  <svg {...base(size)}>
    <path d="M8 5v14l11-7-11-7Z" fill="currentColor" stroke="none" />
  </svg>
);
